import { ChevronDown, Sparkles, Wand2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ensureTextContrast, hasMinimumContrast } from "@/lib/utils";
import { TemplateConfig } from "@/types/email-templates";
import { GenieBranding } from "@/types/ui/branding";

interface ColorSettingsProps {
  config: TemplateConfig;
  isOpen: boolean;
  onToggle: () => void;
  onColorChange: (key: keyof TemplateConfig["colors"], value: string) => void;
  agentBranding?: GenieBranding | null;
  agentName?: string;
}

const ColorSettings = ({
  config,
  isOpen,
  onToggle,
  onColorChange,
  agentBranding,
  agentName,
}: ColorSettingsProps) => {
  const generateHarmonicColor = (baseColor: string): string => {
    const hex = baseColor.replace("#", "");
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    // Blend with white (40% color, 60% white) for a light tint
    const rLightened = Math.round(r * 0.4 + 255 * 0.6);
    const gLightened = Math.round(g * 0.4 + 255 * 0.6);
    const bLightened = Math.round(b * 0.4 + 255 * 0.6);

    return `#${rLightened.toString(16).padStart(2, '0')}${gLightened.toString(16).padStart(2, '0')}${bLightened.toString(16).padStart(2, '0')}`;
  };

  const colors = [
    { key: "primary" as const, label: "Primary Color", defaultValue: "#4E9CFF" },
    { key: "accent" as const, label: "Accent Color", defaultValue: "#FF4DB8" },
    { key: "text" as const, label: "Text Color", defaultValue: "#1A1A1A" },
    { key: "background" as const, label: "Background Color", defaultValue: "#F9FAFC" },
    { key: "headerColor" as const, label: "Header Color", defaultValue: "", isOptional: true },
    { key: "sectionBackground" as const, label: "Section Background", defaultValue: "#FAFBFC", isOptional: true, hasGenerator: true },
  ];

  // Extract available colors from agent branding, removing duplicates
  const suggestedColors = agentBranding
    ? ([
        agentBranding.primaryColor || agentBranding.extractedPalette?.suggestedPrimary,
        agentBranding.secondaryColor || agentBranding.extractedPalette?.suggestedSecondary,
        agentBranding.backgroundColor,
        agentBranding.textColor,
        agentBranding.embedStyle?.headerColor,
        ...(agentBranding.extractedPalette?.palette || []),
      ]
        .filter(Boolean)
        .reduce((unique: string[], color) => {
          const normalized = color.toUpperCase();
          if (!unique.some((c) => c.toUpperCase() === normalized)) {
            unique.push(color);
          }
          return unique;
        }, []) as string[])
    : [];

  const handleSuggestedColorClick = (color: string) => {
    // Apply as primary color by default
    onColorChange("primary", color);
  };

  const handleColorChange = (key: keyof TemplateConfig["colors"], value: string) => {
    let finalValue = value;

    // Ensure text color never becomes white and has proper contrast
    if (key === "text") {
      finalValue = ensureTextContrast(value, config.colors?.background || "#F9FAFC");
    }

    // If changing background, auto-adjust text color if needed for contrast
    if (key === "background" && config.colors?.text) {
      const currentText = config.colors.text;
      const adjustedText = ensureTextContrast(currentText, value);
      if (adjustedText !== currentText) {
        onColorChange("text", adjustedText);
      }
    }

    onColorChange(key, finalValue);
  };

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <CardTitle className="text-lg flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-gradient-to-r from-blue-500 to-pink-500" />
              Colors
              <ChevronDown
                className="ml-auto h-4 w-4 transition-transform"
                style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
              />
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-4">
            {/* Suggested colors from agent */}
            {suggestedColors.length > 0 && (
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-3 border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs font-medium text-gray-700">
                    Colors from {agentName || "Genie"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {suggestedColors.map((color, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSuggestedColorClick(color)}
                      className="group relative w-8 h-8 rounded-md border-2 border-gray-200 hover:border-primary hover:scale-110 transition-all shadow-sm"
                      style={{ backgroundColor: color }}
                      title={`Click to apply ${color}`}
                    >
                      <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                        {color}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">Click a color to apply it as Primary</p>
              </div>
            )}

            {colors.map(({ key, label, defaultValue, isOptional, hasGenerator }) => {
              const currentValue = config.colors?.[key] || defaultValue;
              const isTextColor = key === "text";
              const bgColor = config.colors?.background || "#F9FAFC";
              const hasContrast = !isTextColor || hasMinimumContrast(currentValue, bgColor);

              const handleGenerateHarmonic = (sourceColor: "primary" | "accent") => {
                const baseColor = config.colors?.[sourceColor] || (sourceColor === "primary" ? "#4E9CFF" : "#FF4DB8");
                const harmonicColor = generateHarmonicColor(baseColor);
                handleColorChange(key, harmonicColor);
              };

              return (
                <div key={key}>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">{label}</Label>
                    {isTextColor && !hasContrast && (
                      <span className="text-xs text-amber-600 font-medium">⚠ Low contrast</span>
                    )}
                  </div>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={currentValue}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      className="h-10 w-16 rounded cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={config.colors?.[key] || ""}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      placeholder={isOptional ? "Leave empty for default" : defaultValue}
                      className="text-sm flex-1"
                    />
                    {hasGenerator && (
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => handleGenerateHarmonic("primary")}
                          className="px-2 py-1 rounded text-xs bg-accent/10 text-accent hover:bg-accent/20 transition-colors flex items-center gap-1"
                          title="Generate from Primary"
                        >
                          <Wand2 className="h-3 w-3" />
                          Primary
                        </button>
                        <button
                          type="button"
                          onClick={() => handleGenerateHarmonic("accent")}
                          className="px-2 py-1 rounded text-xs bg-primary/10 text-primary hover:bg-primary/20 transition-colors flex items-center gap-1"
                          title="Generate from Accent"
                        >
                          <Wand2 className="h-3 w-3" />
                          Accent
                        </button>
                      </div>
                    )}
                  </div>
                  {isOptional && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {key === "headerColor" ? "Override for header section (uses primary color if empty)" : "Light tint background for sections (uses #FAFBFC if empty)"}
                    </p>
                  )}
                </div>
              );
            })}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default ColorSettings;
